// @djsem/analysis-contract — Library Metadata Intelligence (Prompt 05).
//
// Reviewable, honesty-first track metadata: genre / mood / energy classification used by
// Prep Workspace, Smart Crates, Chapter Builder, Set Builder, and Show Pack export. This
// is PREP-side only — live runtime may later consume APPROVED metadata but must never
// classify tracks live (hard rule 4). Nothing here is a renderer command (rule 7).
//
// Design rule: never present uncertain metadata as certain. Fallback/unknown states are
// explicit, confidence is clamped by source, and manual review always wins over generated
// values (generated packages are never mutated — reviews produce a new object, rule 6/11).

import {
  vArray, vBoolean, vNumber, vObject, vString, vUnion, vUnit, vRefine,
  type Validator, type DJSEMValidationResult, toDJSEMValidationResult,
} from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** How a genre classification was derived. */
export type DJSEMGenreSource =
  | "manual" | "metadata_tag" | "analysis" | "imported" | "fallback" | "unknown";

/** A coarse energy level label. */
export type DJSEMEnergyLabel = "low" | "medium" | "high" | "peak" | "unknown";
/** Human-review status of a piece of library metadata. */
export type DJSEMReviewStatus =
  | "not_reviewed" | "needs_review" | "reviewed" | "approved" | "rejected";

/** A track's genre classification: primary/secondary/subgenres, confidence, and provenance. */
export interface DJSEMGenreClassification {
  primaryGenre: string;
  secondaryGenres: string[];
  subgenres: string[];
  confidence: number;
  source: DJSEMGenreSource;
  warnings: string[];
  reviewed: boolean;
}

/** A track's mood classification: mood tags, emotional tone, and confidence. */
export interface DJSEMMoodClassification {
  moodTags: string[];
  emotionalTone?: string;
  energyLabel?: DJSEMEnergyLabel;
  confidence: number;
  source: "manual" | "analysis" | "metadata_tag" | "fallback" | "unknown";
  warnings: string[];
  reviewed: boolean;
}

/** A single point on a track's energy curve. */
export interface DJSEMEnergyCurvePoint {
  startTimeMs?: number; endTimeMs?: number;
  startBar?: number; endBar?: number;
  energy: number; confidence: number;
}

/** A track's overall energy profile: score, label, optional curve, and confidence. */
export interface DJSEMEnergyProfile {
  energyScore: number;
  energyLabel: DJSEMEnergyLabel;
  energyCurve?: DJSEMEnergyCurvePoint[];
  confidence: number;
  source: "manual" | "analysis" | "audio_analysis" | "fallback" | "unknown";
  warnings: string[];
}

/** The full library metadata intelligence block for a track: genre, mood, energy, tags, and review status. */
export interface DJSEMLibraryMetadataIntelligence {
  preparationTrackId: string;
  genre: DJSEMGenreClassification;
  mood: DJSEMMoodClassification;
  energy: DJSEMEnergyProfile;
  semanticDomains: string[];
  semanticCategories: string[];
  tags: string[];
  warnings: string[];
  confidence: number;
  reviewStatus: DJSEMReviewStatus;
}

/** Input for {@link LibraryMetadataIntelligenceService.classifyTrack}. */
export interface ClassifyTrackInput {
  preparationTrackId: string;
  title?: string;
  artist?: string;
  remix?: string;
  fileGenreTag?: string;        // e.g. an ID3 genre tag
  importedGenres?: string[];
  userTags?: string[];
  existingMoodTags?: string[];
  existingSemanticDomains?: string[];
  existingEnergyScore?: number; // 0..1 if a prior analysis produced one
  durationSeconds?: number;
}

/** Input for {@link LibraryMetadataIntelligenceService.mergeManualReview}: a human's decision plus any field overrides. */
export interface ManualLibraryMetadataReview {
  primaryGenre?: string;
  secondaryGenres?: string[];
  subgenres?: string[];
  moodTags?: string[];
  emotionalTone?: string;
  energyLabel?: DJSEMEnergyLabel;
  tags?: string[];
  note?: string;
  decision: "approve" | "edit" | "reject" | "reset_to_generated";
  reviewedBy?: string;
}

// ---- confidence bands (Prompt 05) ------------------------------------------

/** Confidence-band thresholds used to gate honest metadata presentation (Prompt 05). */
export const CONFIDENCE = {
  strongMin: 0.9,
  usableMin: 0.7,
  weakMin: 0.5,
  fallbackMax: 0.49,
} as const;

const clampUnit = (n: number): number => Math.max(0, Math.min(1, n));

// ---- validators (pure) ------------------------------------------------------

const vEnergyLabel: Validator = vUnion(
  ...(["low", "medium", "high", "peak", "unknown"] as const).map((s) => vString({ enum: [s] })),
);
const vReviewStatus: Validator = vUnion(
  ...(["not_reviewed", "needs_review", "reviewed", "approved", "rejected"] as const).map((s) => vString({ enum: [s] })),
);

/** Structural validator for {@link DJSEMGenreClassification}. */
export const vGenreClassification: Validator = vObject(
  {
    primaryGenre: vString({ min: 1 }),
    secondaryGenres: vArray(vString({ min: 1 })),
    subgenres: vArray(vString({ min: 1 })),
    confidence: vUnit(),
    source: vString({ enum: ["manual", "metadata_tag", "analysis", "imported", "fallback", "unknown"] }),
    warnings: vArray(vString({})),
    reviewed: vBoolean,
  },
);

/** Structural validator for {@link DJSEMMoodClassification}. */
export const vMoodClassification: Validator = vObject(
  {
    moodTags: vArray(vString({ min: 1 })),
    confidence: vUnit(),
    source: vString({ enum: ["manual", "analysis", "metadata_tag", "fallback", "unknown"] }),
    warnings: vArray(vString({})),
    reviewed: vBoolean,
  },
  { emotionalTone: vString({ min: 1 }), energyLabel: vEnergyLabel },
);

/** Structural validator for {@link DJSEMEnergyProfile}. */
export const vEnergyProfile: Validator = vObject(
  {
    energyScore: vUnit(),
    energyLabel: vEnergyLabel,
    confidence: vUnit(),
    source: vString({ enum: ["manual", "analysis", "audio_analysis", "fallback", "unknown"] }),
    warnings: vArray(vString({})),
  },
  {
    energyCurve: vArray(vObject(
      { energy: vUnit(), confidence: vUnit() },
      { startTimeMs: vNumber({ min: 0 }), endTimeMs: vNumber({ min: 0 }), startBar: vNumber({ min: 0 }), endBar: vNumber({ min: 0 }) },
    )),
  },
);

/**
 * Structural + cross-field validator for {@link DJSEMLibraryMetadataIntelligence}: enforces that
 * a fallback/unknown genre source can never claim usable confidence.
 */
export const vLibraryMetadataIntelligence: Validator<DJSEMLibraryMetadataIntelligence> = vRefine(
  vObject({
    preparationTrackId: vString({ min: 1 }),
    genre: vGenreClassification,
    mood: vMoodClassification,
    energy: vEnergyProfile,
    semanticDomains: vArray(vString({ min: 1 })),
    semanticCategories: vArray(vString({ min: 1 })),
    tags: vArray(vString({ min: 1 })),
    warnings: vArray(vString({})),
    confidence: vUnit(),
    reviewStatus: vReviewStatus,
  }) as Validator<DJSEMLibraryMetadataIntelligence>,
  (m) => {
    if (m.genre.source === "fallback" && m.genre.confidence > CONFIDENCE.fallbackMax)
      return `fallback genre confidence must be <= ${CONFIDENCE.fallbackMax}`;
    if (m.genre.primaryGenre === "unknown" && m.genre.confidence >= CONFIDENCE.usableMin)
      return "unknown genre must not carry usable confidence";
    return null;
  },
);

// ---- service (class-based; hard rule 12) ------------------------------------

// A small, well-known genre keyword table. Deliberately conservative: it only maps
// UNAMBIGUOUS tokens, and never guesses a genre from artist name alone.
const GENRE_KEYWORDS: Record<string, string> = {
  house: "House", "tech house": "Tech House", "deep house": "Deep House",
  techno: "Techno", trance: "Trance", "drum and bass": "Drum & Bass", dnb: "Drum & Bass",
  dubstep: "Dubstep", garage: "UK Garage", disco: "Disco", funk: "Funk",
  "hip hop": "Hip-Hop", "hip-hop": "Hip-Hop", rap: "Hip-Hop", rnb: "R&B",
  pop: "Pop", rock: "Rock", ambient: "Ambient", trap: "Trap",
};

const MOOD_KEYWORDS: Record<string, string> = {
  euphoric: "euphoric", dark: "dark", uplifting: "uplifting", chill: "chill",
  aggressive: "aggressive", melancholic: "melancholic", dreamy: "dreamy",
  driving: "driving", emotional: "emotional", hypnotic: "hypnotic",
};

/**
 * Classifies genre/mood/energy for a track from whatever signals are available (metadata tags,
 * imported genres, title tokens), always preferring honest fallback over invented certainty.
 * Manual review always wins over generated values.
 */
export class LibraryMetadataIntelligenceService {
  constructor(
    private readonly genreKeywords: Record<string, string> = GENRE_KEYWORDS,
    private readonly moodKeywords: Record<string, string> = MOOD_KEYWORDS,
  ) {}

  /**
   * Classify a track's genre, mood, and energy from the given signals.
   * @param input - the track identity and any available metadata/analysis signals.
   * @returns the full library metadata intelligence block, confidence-gated and flagged for review if weak.
   */
  classifyTrack(input: ClassifyTrackInput): DJSEMLibraryMetadataIntelligence {
    const genre = this.classifyGenre(input);
    const mood = this.classifyMood(input);
    const energy = this.classifyEnergy(input);

    const warnings: string[] = [];
    if (genre.source === "fallback") warnings.push("Genre is a fallback guess and needs review.");
    if (energy.source === "fallback") warnings.push("Energy is a neutral fallback and needs review.");

    // Overall confidence is the min of the parts — never more certain than the weakest link.
    const confidence = clampUnit(Math.min(genre.confidence, mood.confidence, energy.confidence));
    const reviewStatus: DJSEMReviewStatus = confidence < CONFIDENCE.weakMin ? "needs_review" : "not_reviewed";

    return {
      preparationTrackId: input.preparationTrackId,
      genre, mood, energy,
      semanticDomains: [...(input.existingSemanticDomains ?? [])],
      semanticCategories: [],
      tags: this.dedupe([...(input.userTags ?? []), ...(input.importedGenres ?? [])]),
      warnings,
      confidence,
      reviewStatus,
    };
  }

  /**
   * Merge a manual review into generated metadata. Manual review always wins; returns a NEW
   * object and never mutates the generated input.
   * @param intelligence - the generated (or previously reviewed) metadata block.
   * @param review - the human reviewer's decision and any field overrides.
   * @returns the merged metadata block.
   */
  mergeManualReview(
    intelligence: DJSEMLibraryMetadataIntelligence,
    review: ManualLibraryMetadataReview,
  ): DJSEMLibraryMetadataIntelligence {
    const next: DJSEMLibraryMetadataIntelligence = structuredCloneSafe(intelligence);

    if (review.decision === "reject") {
      next.reviewStatus = "rejected";
      return next;
    }
    if (review.decision === "reset_to_generated") {
      // caller is responsible for supplying the original generated object; we only flag it
      next.reviewStatus = "not_reviewed";
      return next;
    }

    // approve or edit
    if (review.primaryGenre !== undefined) { next.genre.primaryGenre = review.primaryGenre; next.genre.reviewed = true; next.genre.source = "manual"; }
    if (review.secondaryGenres) next.genre.secondaryGenres = [...review.secondaryGenres];
    if (review.subgenres) next.genre.subgenres = [...review.subgenres];
    if (review.moodTags) { next.mood.moodTags = [...review.moodTags]; next.mood.reviewed = true; next.mood.source = "manual"; }
    if (review.emotionalTone !== undefined) next.mood.emotionalTone = review.emotionalTone;
    if (review.energyLabel !== undefined) { next.energy.energyLabel = review.energyLabel; next.mood.energyLabel = review.energyLabel; }
    if (review.tags) next.tags = this.dedupe(review.tags);

    // Manual, reviewed metadata is trustworthy → high confidence.
    if (review.primaryGenre !== undefined) next.genre.confidence = 0.95;
    if (review.moodTags) next.mood.confidence = 0.95;
    next.confidence = clampUnit(Math.min(next.genre.confidence, next.mood.confidence, next.energy.confidence));
    next.reviewStatus = review.decision === "approve" ? "approved" : "reviewed";
    return next;
  }

  /**
   * Validate a value as a well-formed {@link DJSEMLibraryMetadataIntelligence} block.
   * @param input - the (untyped) value to validate.
   * @returns validation result.
   */
  validateIntelligence(input: unknown): DJSEMValidationResult {
    return toDJSEMValidationResult(vLibraryMetadataIntelligence(input), "library_metadata");
  }

  // ---- private classifiers --------------------------------------------------

  private classifyGenre(input: ClassifyTrackInput): DJSEMGenreClassification {
    // 1. explicit metadata tag is the strongest conservative signal
    const fromTag = input.fileGenreTag ? this.matchGenre(input.fileGenreTag) : null;
    if (fromTag) {
      return { primaryGenre: fromTag, secondaryGenres: [], subgenres: [], confidence: 0.8, source: "metadata_tag", warnings: [], reviewed: false };
    }
    // 2. imported genres (from a prior library) — usable but reviewable
    const fromImport = (input.importedGenres ?? []).map((g) => this.matchGenre(g)).find(Boolean);
    if (fromImport) {
      return { primaryGenre: fromImport, secondaryGenres: [], subgenres: [], confidence: 0.72, source: "imported", warnings: [], reviewed: false };
    }
    // 3. title/remix tokens — weak, must be reviewed, never high confidence
    const fromTitle = input.title ? this.matchGenre(input.title) : null;
    if (fromTitle) {
      return { primaryGenre: fromTitle, secondaryGenres: [], subgenres: [], confidence: 0.55, source: "analysis", warnings: ["Genre inferred from title text; review recommended."], reviewed: false };
    }
    // 4. honest fallback — unknown, low confidence (never invented from artist alone)
    return {
      primaryGenre: "unknown", secondaryGenres: [], subgenres: [], confidence: 0.2,
      source: "fallback", warnings: ["No reliable genre signal; defaulted to unknown."], reviewed: false,
    };
  }

  private classifyMood(input: ClassifyTrackInput): DJSEMMoodClassification {
    const tags = this.dedupe(
      [...(input.existingMoodTags ?? []), ...(input.userTags ?? [])]
        .map((t) => this.moodKeywords[t.toLowerCase()])
        .filter((x): x is string => Boolean(x)),
    );
    if (tags.length) {
      return { moodTags: tags, confidence: 0.75, source: input.existingMoodTags?.length ? "analysis" : "metadata_tag", warnings: [], reviewed: false };
    }
    return { moodTags: [], confidence: 0.2, source: "fallback", warnings: ["No mood signal; needs review."], reviewed: false };
  }

  private classifyEnergy(input: ClassifyTrackInput): DJSEMEnergyProfile {
    if (typeof input.existingEnergyScore === "number") {
      const score = clampUnit(input.existingEnergyScore);
      return { energyScore: score, energyLabel: this.energyLabel(score), confidence: 0.75, source: "analysis", warnings: [] };
    }
    // honest neutral fallback
    return { energyScore: 0.5, energyLabel: "unknown", confidence: 0.2, source: "fallback", warnings: ["No energy analysis; neutral fallback."] };
  }

  private matchGenre(text: string): string | null {
    const t = text.toLowerCase();
    // Match the MOST SPECIFIC keyword first ("tech house" before "house").
    const keys = Object.keys(this.genreKeywords).sort((a, b) => b.length - a.length);
    for (const k of keys) {
      if (t.includes(k)) return this.genreKeywords[k];
    }
    return null;
  }

  private energyLabel(score: number): DJSEMEnergyLabel {
    if (score >= 0.85) return "peak";
    if (score >= 0.6) return "high";
    if (score >= 0.35) return "medium";
    return "low";
  }

  private dedupe(xs: string[]): string[] { return [...new Set(xs)]; }
}

// structuredClone exists in modern runtimes; fall back to JSON for older ones.
function structuredCloneSafe<T>(v: T): T {
  return typeof structuredClone === "function" ? structuredClone(v) : JSON.parse(JSON.stringify(v));
}

/**
 * Build an honest fallback metadata block for a track we know almost nothing about.
 * @param preparationTrackId - the track's preparation id.
 * @returns a low-confidence, fallback-sourced metadata block.
 */
export function buildFallbackLibraryMetadata(preparationTrackId: string): DJSEMLibraryMetadataIntelligence {
  return new LibraryMetadataIntelligenceService().classifyTrack({ preparationTrackId });
}
